import { AutomaticAlignmentProgress } from "./AutomaticAlignmentProgress";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { consoleBffRequestUrl } from "@/api/client";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { formatDateTime } from "../app-bindings/attachment-state-ui";
import {
  useAsynchronousExecutionDetail,
  useAppBindingDetail,
  type AsynchronousExecutionDetailResponse,
} from "../app-bindings/hooks/useAppBindings";
import {
  AsynchronousExecutionStatePill,
  AsynchronousExecutionStatusGuidance,
  attachmentStorageAlignmentFailureLabel,
  alignmentCommonFailureGuidance,
  asynchronousExecutionKindLabel,
  asynchronousExecutionReasonLabel,
} from "./asynchronous-execution-ui";

export function AsynchronousExecutionDetailPage() {
  const { executionId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const kintoneAppId = searchParams.get("kintoneAppId") ?? "";
  const { execution, isLoading, error, refresh } = useAsynchronousExecutionDetail(executionId, true);
  const targetApp = useAppBindingDetail(kintoneAppId);
  const historyQuery = new URLSearchParams();
  if (kintoneAppId) historyQuery.set("kintoneAppId", kintoneAppId);
  if (execution) historyQuery.set("executionKind", execution.executionKind);
  const historyUrl = `/console/asynchronous-executions${historyQuery.size ? `?${historyQuery}` : ""}`;
  if (isLoading) return <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />読み込み中</div>;
  if (!execution) return <div className="space-y-4 p-6"><div role="alert" className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">{error ?? "実行が見つかりません。"}</div><Button type="button" variant="outline" onClick={() => void refresh()}>状態を再取得</Button><Button asChild variant="outline"><Link to={historyUrl}>履歴へ戻る</Link></Button></div>;
  const isDiagnosticExecution =
    execution.attachmentStorageAlignmentExecutionConfiguration?.executionMode === "diagnostic_only" ||
    execution.attachmentStorageAlignmentDiagnosticResult?.executionMode === "diagnostic_only";
  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-end justify-between gap-3"><div><h1 className="text-2xl font-semibold">{isDiagnosticExecution ? "添付ファイル診断" : "非同期実行詳細"}</h1><p className="mt-1 font-mono text-xs text-muted-foreground">{execution.executionId}</p></div><Button asChild variant="outline"><Link to={historyUrl}>履歴へ戻る</Link></Button></div>
      {error ? <div role="alert" className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">状態の更新に失敗しました。{error} 表示は最後に取得できた状態です。<Button variant="outline" onClick={() => void refresh()}>状態を再取得</Button></div> : null}
      {!error && !["completed", "failed", "canceled"].includes(execution.executionState) ? <p className="text-sm text-muted-foreground">状態を自動更新しています。処理が完了すると結果を表示します。</p> : null}
      {targetApp.binding?.appBindingId === execution.appBindingId ? <p className="text-sm">対象アプリ：{targetApp.binding.kintoneAppName}（アプリID {kintoneAppId}） <Link className="underline" to={`/console/app-bindings/${encodeURIComponent(kintoneAppId)}#attachment-storage-alignment`}>アプリ設定へ戻る</Link></p> : null}
      <Card>
        <CardHeader><CardTitle>{asynchronousExecutionKindLabel(execution.executionKind)}</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          {execution.executionKind === "attachment_storage_alignment"
            ? <ExecutionConfiguration execution={execution} />
            : null}
          {execution.alignmentOrigin === "automatic" ? <div className="space-y-2"><AutomaticAlignmentProgress execution={execution} />{!execution.automaticAlignmentProgress ? <Button type="button" variant="outline" onClick={() => void refresh()}>自動整合化の進捗を再取得</Button> : null}</div> : <AsynchronousExecutionStatusGuidance execution={execution} />}
          {execution.executionKind === "attachment_recovery_export" && execution.executionState === "completed" ? (
            <div className="space-y-2">
              <Button asChild><a href={consoleBffRequestUrl(`/console-bff/api/app-bindings/${execution.appBindingId}/attachment-recovery-exports/${execution.executionId}/file`)}>復元情報ZIPをダウンロード</a></Button>
              <p className="text-sm text-muted-foreground">生成物は作成から30日間保持されます。ダウンロードしたZIPは復元時に使用します。</p>
            </div>
          ) : null}
          {execution.attachmentStorageAlignmentHandlingResult ? <section aria-label="整合化の処置結果" className="space-y-2 text-sm">
            <p>スキップ: {execution.attachmentStorageAlignmentHandlingResult.skippedFileCount} 件 / 削除: {execution.attachmentStorageAlignmentHandlingResult.deletedFileCount} 件 / 修復: {execution.attachmentStorageAlignmentHandlingResult.repairedFileCount} 件</p>
            <p className="text-muted-foreground">スキップした対象は変更していません。処理失敗・停止の有無は実行状態と理由を確認してください。</p>
            <ul>{execution.attachmentStorageAlignmentHandlingResult.samples.map((item, index) => <li key={index}>
              レコード {item.recordId} / {item.fieldBindingKey}{item.rowKey ? ` / ${item.rowKey}` : ""} / {item.fileName ?? "対象を一意に特定できません"}：{({ skipped: "スキップ", deleted: "削除", repaired: "修復" })[item.disposition]}{item.reasonCode ? `（${item.reasonCode}）` : ""}
            </li>)}</ul>
            {execution.attachmentStorageAlignmentHandlingResult.hasMore ? <p>最新の100件を表示しています。全対象と理由はジョブの処置記録に保持されています。</p> : null}
          </section> : null}
          {execution.attachmentEnvironmentRestoreResult ? <section aria-label="共有復元結果" className="space-y-2 text-sm">
            <p>参照ファイル不在時の扱い: {({ restore_broken_reference: "参照無し添付として復旧", skip: "スキップして続行", stop: "復旧を停止" })[execution.attachmentEnvironmentRestoreResult.missingReferencedFilePolicy]}</p>
            <p>正常復旧: {execution.attachmentEnvironmentRestoreResult.restoredAttachmentCount} 件 / 参照無し復旧: {execution.attachmentEnvironmentRestoreResult.brokenReferenceRestoredAttachmentCount} 件 / スキップ: {execution.attachmentEnvironmentRestoreResult.skippedMissingAttachmentCount} 件</p>
            <p className="text-muted-foreground">確定済みの件数です。参照無し添付は同じ位置へファイルを戻すと、同じ添付からアクセスできます。</p>
          </section> : null}
          <dl className="grid gap-4 text-sm md:grid-cols-2">
            {execution.executionKind === "attachment_storage_alignment" ? <Detail label="起動元">{execution.alignmentOrigin === "automatic" ? "自動整合化" : execution.alignmentOrigin === "single_record" ? "単一レコード指定" : execution.alignmentOrigin === "manual" ? "手動" : "未確認"}</Detail> : null}
            <Detail label="状態"><AsynchronousExecutionStatePill state={execution.executionState} startedAt={execution.startedAt} /></Detail>
            <Detail label="理由">
              <div className="space-y-1">
                <p>{asynchronousExecutionReasonLabel(execution.reasonCode)}</p>
                {execution.commonFailureCount ? <p>共通障害の連続失敗：{execution.commonFailureCount}回。{alignmentCommonFailureGuidance(execution)}</p> : null}
                {execution.attachmentStorageAlignmentFailureCode
                  ? <p>{attachmentStorageAlignmentFailureLabel(execution.attachmentStorageAlignmentFailureCode)}</p>
                  : null}
              </div>
            </Detail>
            <Detail label="開始要求">{formatDateTime(execution.createdAt)}</Detail>
            <Detail label="処理開始">{formatDateTime(execution.startedAt)}</Detail>
            <Detail label="完了">{formatDateTime(execution.finishedAt)}</Detail>
            <Detail label="表示保持期限">{formatDateTime(execution.retainUntil)}</Detail>
            <Detail label="更新番号">{execution.revision}</Detail>
            {execution.executionKind === "attachment_storage_alignment" &&
              execution.attachmentStorageAlignmentExecutionConfiguration?.modeId ===
                "attachment_compatible" ? (
                <Detail label="外部残存ファイルの扱い">
                  {externalResidualFileHandlingLabel(
                    execution.attachmentStorageAlignmentExecutionConfiguration.executionOptions
                      .externalResidualFileHandling,
                  )}
                </Detail>
              ) : null}
          </dl>
        </CardContent>
      </Card>
      {execution.executionKind === "attachment_storage_alignment" && isDiagnosticExecution
        ? <DiagnosticResult execution={execution} />
        : null}
      <p className="text-sm text-muted-foreground">取消・再開は対象の kintone アプリ設定にある用途別操作から行います。監査証跡は監査ログで確認できます。</p>
    </div>
  );
}

type DiagnosticResultValue = NonNullable<
  AsynchronousExecutionDetailResponse["attachmentStorageAlignmentDiagnosticResult"]
>;

function ExecutionConfiguration({ execution }: { execution: AsynchronousExecutionDetailResponse }) {
  const configuration = execution.attachmentStorageAlignmentExecutionConfiguration;
  if (!configuration) {
    return (
      <section className="text-sm text-muted-foreground">
        保存済みの実行モードと実行オプションを確認できません。画面を再読み込みして再取得してください。
      </section>
    );
  }
  const options = configuration.executionOptions;
  const modeIsDiagnostic = configuration.executionMode === "diagnostic_only";
  const enabledOptions = [
    options.migrateExternalStorageFilesToStandardStorage ? "外部添付を標準添付へ移行" : null,
    options.migrateStandardStorageFilesToExternalStorage ? "標準添付を外部添付へ移行" : null,
    options.repairStoragePlacementDrift ? "配置のずれを修復" : null,
    options.repairCompatibleThumbnailCarriers ? "互換サムネイルを修復" : null,
    options.moveMigratedExternalSourceFilesToTrash ? "移行済みの外部元ファイルを隔離" : null,
    options.deleteConfirmedBrokenExternalAttachmentDisplay ? "参照切れ確認済みの添付表示を削除" : null,
  ].filter((label): label is string => label !== null);
  return (
    <section className="grid gap-4 text-sm md:grid-cols-2">
      <Detail label="実行モード">
        <p className="font-medium">{modeIsDiagnostic ? "診断のみ" : "整合化"}</p>
      </Detail>
      <Detail label="無効な証跡・サムネイルの扱い"><p>{options.invalidSpecialFileHandling === "delete" ? "kintone上の対象添付を削除" : "スキップ（変更しない）"}</p></Detail>
      <Detail label="実行オプション">
        {enabledOptions.length > 0 ? (
          <ul aria-label="有効な実行オプション" className="space-y-1">
            {enabledOptions.map((label) => <li key={label}>{label}</li>)}
          </ul>
        ) : <p className="text-muted-foreground">なし</p>}
      </Detail>
    </section>
  );
}

function externalResidualFileHandlingLabel(
  value: NonNullable<
    AsynchronousExecutionDetailResponse["attachmentStorageAlignmentExecutionConfiguration"]
  >["executionOptions"]["externalResidualFileHandling"],
): string {
  switch (value) {
    case "none":
      return "何もしない";
    case "restore_to_record":
      return "レコードに取り込む";
    case "move_to_trash":
      return "隔離領域へ移動";
  }
}

function DiagnosticResult({ execution }: { execution: AsynchronousExecutionDetailResponse }) {
  const result = execution.attachmentStorageAlignmentDiagnosticResult;
  if (!result) {
    return (
      <Card>
        <CardHeader><CardTitle>診断結果</CardTitle></CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          {["queued", "running", "blocked", "cancel_requested"].includes(execution.executionState)
            ? "診断結果は処理の進行に合わせて表示します。"
            : "この実行には表示できる診断結果が保存されていません。現在の状態を確認するには新しい診断を開始してください。"}
        </CardContent>
      </Card>
    );
  }
  const exhausted = result.scanTerminalState === "exhausted";
  const rangeComplete = execution.executionState === "completed" && result.scanTerminalState === "record_limit_reached";
  return (
    <Card>
      <CardHeader><CardTitle>診断結果</CardTitle></CardHeader>
      <CardContent className="space-y-5">
        <div className={[
          "rounded-md border p-3 text-sm",
          exhausted && result.findingCount === 0
            ? "border-emerald-300 bg-emerald-50 text-emerald-950"
            : "border-amber-300 bg-amber-50 text-amber-950",
        ].join(" ")}>
          {rangeComplete ? `選択した最大${execution.recordProcessingScope?.maxRecordCount ?? result.scannedRecordCount}レコードの診断が完了しました。全アプリの診断は未完了です。` : exhausted
            ? result.findingCount === 0
              ? "対象範囲の走査が完了し、対応が必要な添付候補は見つかりませんでした。"
              : `対象範囲の走査が完了し、${result.findingCount}件の確認・対応候補が見つかりました。`
            : execution.executionState === "failed" || execution.executionState === "canceled"
              ? "診断は全件走査の完了前に終了しました。表示件数は確認済みページまでの集計です。"
              : execution.executionState === "blocked"
                ? "診断は要対応のため停止しています。表示件数は確認済みページまでの集計です。"
                : "診断は進行中です。表示件数は確認済みページまでの集計です。"}
        </div>
        <dl className="grid gap-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
          <Detail label="走査したレコード">{result.scannedRecordCount}件</Detail>
          <Detail label="検査した添付候補">{result.inspectedAttachmentCount}件</Detail>
          <Detail label="確認・対応候補">{result.findingCount}件</Detail>
          <Detail label="開始時の最大レコードID">{result.startHighWaterRecordId ?? "対象レコードなし"}</Detail>
          <Detail label="処理済みレコードID">{result.processedRecordIdPointer ?? "-"}</Detail>
          <Detail label="範囲走査の確認時刻">{formatDateTime(result.confirmedAt)}</Detail>
          <Detail label="走査状態">{exhausted ? "全件走査済み" : rangeComplete ? "選択範囲完了" : "走査中"}</Detail>
          <Detail label="最大レコード数">{execution.recordProcessingScope?.maxRecordCount ?? "保存情報なし"}</Detail>
          <Detail label="走査順">レコードID昇順</Detail>
          <Detail label="1ページの上限">{result.recordScanLimit}レコード</Detail>
        </dl>
        <CountList
          title="推奨対応"
          entries={result.plannedActionCounts.map((entry) => ({
            key: entry.plannedAction,
            label: plannedActionLabel(entry.plannedAction),
            count: entry.count,
          }))}
          emptyText="判定対象はありません。"
        />
        {result.manualReviewReasonCounts.length > 0 ? (
          <CountList
            title="手動確認の理由"
            entries={result.manualReviewReasonCounts.map((entry) => ({
              key: entry.reasonCode,
              label: manualReviewReasonLabel(entry.reasonCode),
              count: entry.count,
            }))}
            emptyText="手動確認が必要な理由はありません。"
          />
        ) : null}
        <CountList
          title="添付状態"
          entries={result.attachmentStateCounts.map((entry) => ({
            key: entry.attachmentFileState,
            label: attachmentStateLabel(entry.attachmentFileState),
            count: entry.count,
          }))}
          emptyText="添付候補はありません。"
        />
        {result.thumbnailFindingCounts.length > 0 ? (
          <CountList
            title="サムネイル所見"
            entries={result.thumbnailFindingCounts.map((entry) => ({
              key: entry.thumbnailFindingReason,
              label: thumbnailFindingLabel(entry.thumbnailFindingReason),
              count: entry.count,
            }))}
            emptyText="サムネイル所見はありません。"
          />
        ) : null}
      </CardContent>
    </Card>
  );
}

function CountList({
  title,
  entries,
  emptyText,
}: {
  title: string;
  entries: Array<{ key: string; label: string; count: number }>;
  emptyText: string;
}) {
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-medium">{title}</h3>
      {entries.length === 0 ? (
        <p className="text-sm text-muted-foreground">{emptyText}</p>
      ) : (
        <ul className="grid gap-2 text-sm sm:grid-cols-2">
          {entries.map((entry) => (
            <li key={entry.key} className="flex justify-between gap-3 rounded-md border px-3 py-2">
              <span>{entry.label}</span><span className="font-medium">{entry.count}件</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function plannedActionLabel(value: DiagnosticResultValue["plannedActionCounts"][number]["plannedAction"]) {
  const labels = {
    standard_storage_migration: "標準添付へ移行",
    external_storage_migration: "外部添付へ移行",
    storage_placement_repair: "外部配置を修復",
    external_residual_trash_move: "外部元ファイルを隔離",
    invalid_special_file_skip: "無効な特殊ファイルをスキップ",
    invalid_special_file_delete: "無効な特殊ファイルを削除",
    compatible_thumbnail_diagnostic: "サムネイルを確認",
    compatible_thumbnail_repair: "サムネイルを修復",
    no_action: "対応不要",
    reference_broken: "参照切れを確認",
    reference_unconfirmed: "参照確認を再試行",
    proof_scope_reconstruction: "証跡スコープを再構成",
    lock_conflict_retryable: "編集中ロックの解消後に再試行",
    revision_mismatch_retryable: "最新レコードで再試行",
    snapshot_mismatch_retryable: "設定再確認後に再試行",
    blocked: "手動確認が必要",
  } satisfies Record<typeof value, string>;
  return labels[value];
}

function attachmentStateLabel(value: DiagnosticResultValue["attachmentStateCounts"][number]["attachmentFileState"]) {
  const labels = {
    normal_standard: "標準添付（正常）",
    normal_external: "外部添付（正常）",
    external_unmigrated: "外部添付への未移行",
    standard_unmigrated: "標準添付への未移行",
    migration_preparing: "移行準備中",
    migration_record_update_pending: "レコード更新待ち",
    source_carrier_cleanup_pending: "移行元整理待ち",
    migration_blocked: "移行停止",
    migration_failed: "移行失敗",
    placement_reconciliation_required: "配置・証跡の補正が必要",
    reference_broken: "参照切れ",
    reference_unconfirmed: "参照確認不能",
  } satisfies Record<typeof value, string>;
  return labels[value];
}

function thumbnailFindingLabel(value: DiagnosticResultValue["thumbnailFindingCounts"][number]["thumbnailFindingReason"]) {
  const labels = {
    thumbnail_missing: "サムネイルなし",
    thumbnail_unexpected: "不要なサムネイルあり",
    thumbnail_size_mismatch: "サムネイルサイズ不一致",
    thumbnail_identity_mismatch: "サムネイル対応関係不一致",
    thumbnail_duplicate_or_ambiguous: "サムネイル重複・判定不能",
    thumbnail_carrier_broken: "サムネイル参照切れ",
  } satisfies Record<typeof value, string>;
  return labels[value];
}

function manualReviewReasonLabel(value: DiagnosticResultValue["manualReviewReasonCounts"][number]["reasonCode"]) {
  const labels = {
    proof_missing: "証跡が見つかりません。証跡ファイルを確認（proof_missing）",
    proof_invalid: "証跡を検証できません。証跡内容と対象範囲を確認（proof_invalid）",
    carrier_structure_ambiguous: "添付欄の構成を一意に判定できません。添付欄を確認（carrier_structure_ambiguous）",
    migration_incomplete: "移行途中の構成が残っています。移行状態を確認（migration_incomplete）",
    storage_scope_unresolved: "外部ファイルの対象範囲を確定できません。レコード・フィールド・行の対応と添付済み証跡を確認（storage_scope_unresolved）",
    other_manual_review: "その他の理由で自動判定できません。監査情報を確認（other_manual_review）",
  } satisfies Record<typeof value, string>;
  return labels[value];
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><dt className="text-xs text-muted-foreground">{label}</dt><dd className="mt-1">{children}</dd></div>;
}
